
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function About() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const sectors = [
  t('sector.energy'),
  t('sector.technology'),
  t('sector.manufacturing'),
  t('sector.electronics'),
  t('sector.industrial'),
  t('sector.agricultural'),
  t('sector.tourism'),
  t('sector.healthcare'),
  t('sector.commercial'),
  t('sector.oilgas'),
  t('sector.educational'),
];

  return (
    <section id="about" className="py-32 bg-cream relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Pattern */}
      <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-1/2 h-full opacity-5`}>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 relative">
        {/* Section Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
          <div className="max-w-2xl">
            <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-12 h-px bg-gold"></div>
              <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('about.label')}</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-6xl text-navy leading-tight">
              {t('about.title1')}
              <span className="block text-gold">{t('about.title2')}</span>
            </h2>
          </div>
          <p className={`text-lg text-charcoal-light max-w-md leading-relaxed ${isRTL ? 'text-right' : ''}`}>
            {t('about.description')}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column - Image */}
          <div className={`lg:col-span-5 relative ${isRTL ? 'lg:order-2' : ''}`}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/homeabout2.jpg"
                  alt="About ALEBEL Investment"
                  className="w-full h-[600px] object-cover object-center"
                />
              </div>
              
              {/* Experience Badge */}
              <div className={`absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} bg-navy rounded-2xl p-6 shadow-xl`}>
                <div className="text-center">
                  <div className="text-4xl font-serif text-gold">2019</div>
                  <div className="text-white/80 text-sm mt-1">{t('hero.established')}</div>
                </div>
              </div>

              {/* Decorative */}
              <div className={`absolute -top-6 ${isRTL ? '-right-6' : '-left-6'} w-32 h-32 border-2 border-gold/20 rounded-3xl -z-10`}></div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className={`lg:col-span-7 space-y-8 ${isRTL ? 'lg:order-1' : ''}`}>
            {/* Description Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-2xl border border-cream-dark hover:shadow-xl hover:border-gold/20 transition-all duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-building-4-line text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">{t('about.parentEntity')}</h3>
                <p className="text-charcoal-light leading-relaxed text-sm">
                  {t('about.parentEntityDesc')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-cream-dark hover:shadow-xl hover:border-gold/20 transition-all duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-global-line text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">{t('about.uaeBased')}</h3>
                <p className="text-charcoal-light leading-relaxed text-sm">
                  {t('about.uaeBasedDesc')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-cream-dark hover:shadow-xl hover:border-gold/20 transition-all duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-funds-line text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">{t('about.activeOwnership')}</h3>
                <p className="text-charcoal-light leading-relaxed text-sm">
                  {t('about.activeOwnershipDesc')}
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl border border-cream-dark hover:shadow-xl hover:border-gold/20 transition-all duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-xl flex items-center justify-center mb-5">
                  <i className="ri-lightbulb-flash-line text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">{t('about.innovationFocus')}</h3>
                <p className="text-charcoal-light leading-relaxed text-sm">
                  {t('about.innovationFocusDesc')}
                </p>
              </div>
            </div>

            {/* Investment Sectors */}
            <div className="bg-navy rounded-2xl p-8">
              <h4 className="text-white font-semibold mb-4">{t('about.investmentSectors')}</h4>
              <div className={`flex flex-wrap gap-3 ${isRTL ? 'justify-end' : ''}`}>
                {sectors.map((sector) => (
<span key={sector} className="px-3 py-1.5 md:px-4 md:py-2 bg-white/10 text-white/90 text-xs md:text-sm rounded-full border border-white/10">                    {sector}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/about"
              className={`inline-flex items-center gap-3 text-gold font-semibold hover:gap-5 transition-all duration-300 cursor-pointer group ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              {t('about.learnMore')}
              <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line group-hover:${isRTL ? '-translate-x-1' : 'translate-x-1'} transition-transform`}></i>
            </Link>
          </div>
        </div>

        {/* CEO Message */}
        <div className="mt-32 relative">
          <div className="bg-gradient-to-br from-navy via-navy-light to-navy rounded-3xl p-12 lg:p-16 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-96 h-96 bg-gold rounded-full blur-3xl`}></div>
            </div>

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">

  {/* CEO Image — shows first on mobile */}
  <div className={`lg:col-span-4 ${isRTL ? 'lg:order-1' : 'lg:order-2'}`}>
    <div className="relative">
      <div className="w-40 h-40 md:w-56 md:h-56 mx-auto rounded-full overflow-hidden border-4 border-gold/30 shadow-2xl bg-white">
  <img
    src="/Gemini_Generated_Image_w6nuwpw6nuwpw6nu.png"
    alt="CEO"
    className="w-full h-full object-contain object-center scale-125"
  />
</div>
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold to-gold-dark px-6 py-2 rounded-full shadow-lg">
        <span className="text-navy text-sm font-semibold">CEO</span>
      </div>
    </div>
    <div className="text-center mt-6">
     <div className="text-white font-semibold text-lg md:text-xl">{t('about.mhbm')}</div>
    <div className="text-white font-semibold text-lg md:text-xl">{t('about.ceoName')}</div>
      <div className="text-gold text-sm">{t('about.ceoTitle')}</div>
    </div>
  </div>

  {/* Quote — shows second on mobile */}
  <div className={`lg:col-span-8 space-y-6 md:space-y-8 ${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
    <i className={`ri-double-quotes-${isRTL ? 'r' : 'l'} text-5xl md:text-6xl text-gold/30`}></i>
    <div className="space-y-4 md:space-y-6">
      <p className="text-lg md:text-xl lg:text-2xl text-white/95 leading-relaxed font-light">{t('about.ceoQuote1')}</p>
      <p className="text-lg md:text-xl lg:text-2xl text-white/95 leading-relaxed font-light">{t('about.ceoQuote2')}</p>
    </div>
  </div>

</div>
          </div>
        </div>
      </div>
    </section>
  );
}
